import { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, useMap } from "react-leaflet";
import L from "leaflet";

import { HeatmapLayer } from "react-leaflet-heatmap-layer-v3";
import { If, Then, Else } from "react-if";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup";
import ToggleButton from "react-bootstrap/ToggleButton";

import { useQuery } from "react-query";

const lvccTileBounds = [
  [36.128, -115.166],
  [36.14, -115.148],
];

// The trip is over and the data will never change, so the archive reads a static
// export instead of querying InfluxDB on every page load. Regenerate with
// `node scripts/export-dc34.mjs`. Points are [minutesFromStart, lat, lon],
// downsampled to one a minute and already clipped to the Las Vegas area.
const ARCHIVE_URL = "/dc34.json";

const CON_TZ = "America/Los_Angeles";
// en-CA formats as YYYY-MM-DD, which sorts lexically.
const dayKey = (d) => d.toLocaleDateString("en-CA", { timeZone: CON_TZ });
const dayLabel = (d) =>
  d.toLocaleDateString("en-US", {
    timeZone: CON_TZ,
    weekday: "short",
    month: "short",
    day: "numeric",
  });

// Readable against the dark jawg-matrix tiles.
const dayColors = ["#4dd0e1", "#81c784", "#ffd54f", "#ff8a65", "#ba68c8"];

const sampleLocation = (s) => [s.latitude, s.longitude, 0.2];
const sampleLatLng = (s) => L.latLng(s.latitude, s.longitude);

const tileLayer = {
  attribution:
    '<a href="http://jawg.io" title="Tiles Courtesy of Jawg Maps" target="_blank">&copy; <b>Jawg</b>Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  url: "https://{s}.tile.jawg.io/jawg-matrix/{z}/{x}/{y}{r}.png?access-token=s7Jaxor8MDBUEQ6Wz8l5vjpEPu5gfyJ3GEO8CstO3xVnU5mDfX38S4o4LHjyYRYQ",
  maxZoom: 22,
};

// The Courtyard by Marriott, about 450m south of the West Hall. Sleeping there
// accounts for 41% of every sample taken all week, which buries everywhere I
// actually went under one enormous blob. Center is the median overnight
// (1am-7am) position; keeping one sample in fifteen leaves the hotel visible as
// a hot spot without letting it dominate.
const hotel = L.latLng(36.13038, -115.15607);
const hotelRadius = 100;
const hotelThinning = 15;

function atHotel(sample) {
  return hotel.distanceTo(sampleLatLng(sample)) <= hotelRadius;
}

function thinHotel(samples) {
  let seen = 0;
  return samples.filter((s) => !atHotel(s) || seen++ % hotelThinning === 0);
}

// Gaps in tracking (indoors, watch asleep) show up as long straight jumps, so
// drop any leg over a kilometer instead of counting it as distance covered.
// Legs that start and end at the hotel are GPS drift while asleep, not walking.
function trackMeters(samples) {
  let meters = 0;
  for (let i = 1; i < samples.length; i++) {
    const previous = samples[i - 1];
    const current = samples[i];
    if (atHotel(previous) && atHotel(current)) continue;
    const step = sampleLatLng(previous).distanceTo(sampleLatLng(current));
    if (step < 1000) meters += step;
  }
  return meters;
}

const miles = (meters) => (meters / 1609.344).toFixed(1);

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);
  return null;
}

function DC34(props) {
  const [view, setView] = useState("track");
  const [day, setDay] = useState("all");

  const { isLoading, error, data: samples = [] } = useQuery(
    "dc34",
    async () => {
      const response = await fetch(ARCHIVE_URL);
      if (!response.ok) {
        throw new Error(`Could not load the DC34 archive (${response.status})`);
      }
      const { start, points } = await response.json();
      const startedAt = Date.parse(start);
      return points.map(([minute, latitude, longitude]) => ({
        timestamp: new Date(startedAt + minute * 60000),
        latitude,
        longitude,
      }));
    },
    { refetchInterval: false, refetchOnWindowFocus: false, staleTime: Infinity }
  );

  const days = useMemo(() => {
    const groups = new Map();
    samples.forEach((s) => {
      const key = dayKey(s.timestamp);
      if (!groups.has(key)) {
        groups.set(key, { key, label: dayLabel(s.timestamp), samples: [] });
      }
      groups.get(key).samples.push(s);
    });
    return [...groups.values()]
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((d, index) => ({
        ...d,
        color: dayColors[index % dayColors.length],
        // Distance comes off the full track; only the drawn points are thinned.
        meters: trackMeters(d.samples),
        samples: thinHotel(d.samples),
      }));
  }, [samples]);

  const shownDays = useMemo(
    () => (day === "all" ? days : days.filter((d) => d.key === day)),
    [days, day]
  );

  const displaySamples = useMemo(
    () => shownDays.flatMap((d) => d.samples),
    [shownDays]
  );

  const bounds = useMemo(() => {
    if (displaySamples.length === 0) return undefined;
    return L.latLngBounds(displaySamples.map(sampleLatLng));
  }, [displaySamples]);

  const totalMeters = useMemo(
    () => shownDays.reduce((sum, d) => sum + d.meters, 0),
    [shownDays]
  );

  if (isLoading) {
    return (
      <div className="text-center justify-content-center p-5 align-items-center">
        <Spinner animation="border" role="status" size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center justify-content-center p-5 align-items-center">
        <Alert variant="danger">{error.message}</Alert>
      </div>
    );
  }

  if (displaySamples.length === 0) {
    return (
      <div className="text-center justify-content-center p-5 align-items-center">
        <div>No samples to show</div>
      </div>
    );
  }

  return (
    <>
      <Row className="mt-3">
        <Col md={12} className="text-center">
          <h1>DEF CON 34</h1>
          <p className="text-muted mb-1">
            August 6&ndash;9, 2026 &middot; Las Vegas Convention Center
          </p>
          <p className="text-muted">
            {displaySamples.length.toLocaleString()} points &middot;{" "}
            {miles(totalMeters)} miles covered &middot;{" "}
            {shownDays.length} {shownDays.length === 1 ? "day" : "days"}
          </p>
        </Col>
      </Row>

      <Row>
        <Col md={12} className="text-center justify-content-center align-items-center">
          <ToggleButtonGroup
            type="radio"
            name="day"
            value={day}
            onChange={(v) => setDay(v)}
            className="p-1"
          >
            <ToggleButton value="all">All</ToggleButton>
            {days.map((d) => (
              <ToggleButton key={d.key} value={d.key}>
                {d.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            type="radio"
            name="view"
            value={view}
            onChange={(v) => setView(v)}
            className="p-1"
          >
            <ToggleButton value="heatmap">Heatmap</ToggleButton>
            <ToggleButton value="track">Track</ToggleButton>
          </ToggleButtonGroup>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <MapContainer
            bounds={bounds}
            maxZoom={22}
            scrollWheelZoom
            style={{ height: "70vh", width: "100%" }}
          >
            <FitBounds bounds={bounds} />
            <If condition={view === "track"}>
              <Then>
                {shownDays.map((d) => (
                  <Polyline
                    key={d.key}
                    positions={d.samples.map((s) => [s.latitude, s.longitude])}
                    color={d.color}
                    weight={3}
                    opacity={0.85}
                  />
                ))}
              </Then>
              <Else>
                <HeatmapLayer
                  points={displaySamples.map(sampleLocation)}
                  latitudeExtractor={(m) => m[0]}
                  longitudeExtractor={(m) => m[1]}
                  intensityExtractor={(m) => parseFloat(m[2])}
                  radius={12}
                  blur={15}
                  max={1.0}
                />
              </Else>
            </If>
            <TileLayer {...tileLayer} />
            <TileLayer
              url="/tiles-dc34-lvcc/{z}/{x}/{y}.png"
              attribution=""
              maxZoom={22}
              bounds={lvccTileBounds}
            />
          </MapContainer>
        </Col>
      </Row>

    </>
  );
}

export default DC34;
